import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Input, Modal, Select, Space, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import {
  CheckCircleOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  SlidersOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { fetchClientDetail } from './api'
import { buildClientPayloadFromDetail, statusToLabel } from '@/features/client/clientFormShared'
import type { ClientRow, ClientStatusId } from './types'
import {
  useClientListFilters,
  useClients,
  usePbiGroups,
  useUpdateClientMutation,
  type ClientStatusFilter,
} from './hooks'
import { CreateClientModal } from './components/CreateClientModal'
import { FeatureAccessModal } from './components/FeatureAccessModal'
import { useToast } from '@/contexts/ToastContext'
import { formatPhone } from '@/lib/phone'
import { updateSession, useAuthSession } from '@/lib/session'
import { UserTypes } from '@/types/authorization'

type QuickStatusConfirmState = {
  open: boolean
  row: ClientRow | null
  statusId: ClientStatusId
}

export default function Clients() {
  const navigate = useNavigate()
  const authSession = useAuthSession()
  const { addToast } = useToast()
  const isAdmin = authSession?.currentUserTypeId === UserTypes.Admin

  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [featureAccessClient, setFeatureAccessClient] = useState<ClientRow | null>(null)
  const [quickStatusConfirm, setQuickStatusConfirm] = useState<QuickStatusConfirmState>({
    open: false,
    row: null,
    statusId: 1,
  })
  const [selectingClientId, setSelectingClientId] = useState<string | null>(null)

  const defaultPageSize =
    authSession?.defaultPageSize && authSession.defaultPageSize > 0 ? authSession.defaultPageSize : 25
  const [pagination, setPagination] = useState({ current: 1, pageSize: defaultPageSize })

  const { data: pbiGroups = [] } = usePbiGroups()
  const {
    data: allClients = [],
    isLoading,
    isFetching,
    isError,
    error,
  } = useClients()
  const updateMutation = useUpdateClientMutation()

  const { nameFilter, setNameFilter, statusFilter, setStatusFilter, clientList } =
    useClientListFilters(allClients)

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }))
  }, [nameFilter, statusFilter])

  useEffect(() => {
    setPagination((prev) => {
      const maxPage = Math.max(1, Math.ceil(clientList.length / prev.pageSize))
      const nextCurrent = Math.min(prev.current, maxPage)
      if (nextCurrent === prev.current) return prev
      return { ...prev, current: nextCurrent }
    })
  }, [clientList.length, pagination.pageSize])

  const goToClient = useCallback(
    async (row: ClientRow) => {
      setSelectingClientId(row.id)
      try {
        const detail = await fetchClientDetail(row.id)

        updateSession({
          currentClientId: row.id,
          currentClientName: detail?.name || row.name,
          currentClientTypeId: detail?.typeId ?? row.typeId,
          currentClientShowProjectStatus: Boolean(detail?.showProjectStatus ?? row.showProjectStatus),
          currentClientShowAdvisoryMapping: Boolean(detail?.showAdvisoryMapping ?? row.showAdvisoryMapping),
          platformType:
            detail?.platformType
              ?.map((platformType) => Number(platformType))
              .filter((platformType) => Number.isFinite(platformType)) ?? authSession?.platformType,
        })
      } catch (err) {
        updateSession({
          currentClientId: row.id,
          currentClientName: row.name,
          currentClientTypeId: row.typeId,
          currentClientShowProjectStatus: Boolean(row.showProjectStatus),
          currentClientShowAdvisoryMapping: Boolean(row.showAdvisoryMapping),
        })
        addToast(err instanceof Error ? err.message : 'Failed to refresh selected client details', 'warning')
      } finally {
        setSelectingClientId(null)
      }

      navigate(`/clients/${row.id}`)
    },
    [addToast, authSession?.platformType, navigate]
  )

  const quickStatusToggle = useCallback(
    async (row: ClientRow, statusId: ClientStatusId) => {
      try {
        const detail = await fetchClientDetail(row.id)
        if (!detail) {
          addToast('Unable to load client details', 'error')
          return
        }

        await updateMutation.mutateAsync({
          ...buildClientPayloadFromDetail(detail, pbiGroups, statusId),
          clientId: detail.id,
        })
        addToast(`${row.name} marked ${statusToLabel(statusId).toLowerCase()}`, 'success')
        setQuickStatusConfirm({ open: false, row: null, statusId: 1 })
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Unable to update status'
        addToast(message, 'error')
      }
    },
    [addToast, pbiGroups, updateMutation]
  )

  const columns: ColumnsType<ClientRow> = useMemo(() => {
    const baseColumns: ColumnsType<ClientRow> = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        render: (_, row) => (
          <button
            type="button"
            onClick={() => goToClient(row)}
            className="font-medium text-gray-900 hover:text-brand-600 text-left"
          >
            {row.name}
          </button>
        ),
      },
      {
        title: 'Address',
        dataIndex: 'address',
        key: 'address',
        render: (value: string) => value || '—',
      },
      {
        title: 'Phone',
        dataIndex: 'phone',
        key: 'phone',
        render: (value: string) => formatPhone(value),
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        render: (value: string) => value || '—',
      },
      {
        title: 'Status',
        dataIndex: 'statusId',
        key: 'statusId',
        sorter: (a, b) => a.statusId - b.statusId,
        render: (value: ClientStatusId) =>
          value === 1 ? <Tag color="green">Active</Tag> : <Tag color="default">Inactive</Tag>,
      },
      {
        title: 'Principal Contact',
        dataIndex: 'principalContact',
        key: 'principalContact',
        render: (value: string) => value || '—',
      },
    ]

    if (isAdmin) {
      baseColumns.push({
        title: 'Features',
        key: 'features',
        width: 96,
        render: (_, row) => (
          <Tooltip title="Manage feature access">
            <Button
              type="text"
              icon={<SlidersOutlined />}
              aria-label="Manage feature access"
              onClick={() => setFeatureAccessClient(row)}
            />
          </Tooltip>
        ),
      })
    }

    baseColumns.push(
      {
        title: '',
        key: 'actions',
        width: 118,
        render: (_, row) => (
          <Space size={4}>
            <Tooltip title="Edit client">
              <Button
                type="text"
                icon={<EditOutlined />}
                aria-label="Edit client"
                onClick={() => navigate(`/clients/${row.id}/edit`)}
              />
            </Tooltip>
            <Tooltip title="Set active">
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                aria-label="Set client active"
                disabled={row.statusId === 1}
                onClick={() => setQuickStatusConfirm({ open: true, row, statusId: 1 })}
              />
            </Tooltip>
            <Tooltip title="Set inactive">
              <Button
                type="text"
                danger
                icon={<StopOutlined />}
                aria-label="Set client inactive"
                disabled={row.statusId === 2}
                onClick={() => setQuickStatusConfirm({ open: true, row, statusId: 2 })}
              />
            </Tooltip>
          </Space>
        ),
      },
    )

    return baseColumns
  }, [goToClient, isAdmin, navigate])

  const onTableChange = (nextPagination: TablePaginationConfig) => {
    const nextPageSize = nextPagination.pageSize ?? pagination.pageSize
    setPagination((prev) => ({
      ...prev,
      current: nextPagination.current ?? prev.current,
      pageSize: nextPageSize,
    }))
    if (nextPageSize !== authSession?.defaultPageSize) {
      updateSession({ defaultPageSize: nextPageSize })
    }
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white via-white to-brand-50/40">
      <div
        className="pointer-events-none absolute -top-20 -right-20 h-52 w-52 rounded-full bg-brand-100/60 blur-2xl"
        aria-hidden
      />
      <div className="px-6 pt-5 pb-4 border-b border-brand-100">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-gray-900 tracking-tight">Clients</h1>
            <p className="text-sm text-gray-500 mt-1">List, filter, and manage client records</p>
          </div>
          <Tag color="cyan-inverse" className="px-3 py-1 rounded-full font-medium">
            {clientList.length} records
          </Tag>
        </div>
        <div className="mt-3 h-1.5 w-16 rounded-full bg-gradient-to-r from-brand-400 to-brand-600" aria-hidden />
      </div>

      <div className="p-4 bg-white/70 backdrop-blur-[1px]">
        <Space wrap size={12}>
          <Button icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)} aria-label="Add new client">
            Add New Client
          </Button>
          <Input
            placeholder="Name"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            suffix={<SearchOutlined className="text-brand-500" />}
            style={{ width: 180 }}
            allowClear
          />
          <Select<ClientStatusFilter>
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: 'all', label: 'All statuses' },
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' },
            ]}
            style={{ width: 160 }}
          />
          {(isLoading || isFetching) && (
            <span className="text-sm text-gray-500 inline-flex items-center gap-1.5">
              <span className="w-4 h-4 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
              Loading...
            </span>
          )}
        </Space>
      </div>

      <div className="p-4">
        {isError && (
          <Alert
            type="error"
            showIcon
            className="mb-4"
            message={(error as Error)?.message || 'Failed to load clients'}
          />
        )}
        <Table<ClientRow>
          rowKey="id"
          columns={columns}
          dataSource={clientList}
          loading={isLoading || isFetching || selectingClientId !== null}
          onChange={onTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: clientList.length,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showTotal: (total, range) => `${range[0] ?? 0} - ${range[1] ?? 0} of ${total}`,
          }}
        />
      </div>

      <CreateClientModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        isAdmin={isAdmin}
      />

      <FeatureAccessModal
        open={!!featureAccessClient}
        client={featureAccessClient}
        onClose={() => setFeatureAccessClient(null)}
      />

      <Modal
        open={quickStatusConfirm.open}
        title="Change client status"
        okText={quickStatusConfirm.statusId === 1 ? 'Set Active' : 'Set Inactive'}
        okButtonProps={{
          danger: quickStatusConfirm.statusId === 2,
          loading: updateMutation.isPending,
        }}
        onOk={() => {
          if (!quickStatusConfirm.row) return
          void quickStatusToggle(quickStatusConfirm.row, quickStatusConfirm.statusId)
        }}
        onCancel={() => setQuickStatusConfirm({ open: false, row: null, statusId: 1 })}
      >
        <p className="text-sm text-gray-700">
          Are you sure you want to set <strong>{quickStatusConfirm.row?.name}</strong> to{' '}
          <strong>{statusToLabel(quickStatusConfirm.statusId).toLowerCase()}</strong>?
        </p>
      </Modal>
    </div>
  )
}
